import { useState, useEffect, useCallback } from 'react';
import { getToken } from '@/utils/tokenManager';
import { toast } from 'sonner';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });

export function useSecuritySettings() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSessions, setActiveSessions] = useState([]);
  const [showDeviceSessions, setShowDeviceSessions] = useState(false);
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [strictMode, setStrictMode] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [passwordModalConfig, setPasswordModalConfig] = useState({ title: '', description: '' });

  const fetchUserData = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, { headers: getHeaders() });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        setStrictMode(userData.strict_security_mode || false);
        setTwoFactorStatus({ enabled: userData.has_2fa || false, method: userData.preferred_2fa_method || null });
      }
    } catch (err) {
      logger.error('Error fetching user data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchActiveSessions = useCallback(async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/sessions`, { headers: getHeaders() });
      if (response.ok) {
        const data = await response.json();
        setActiveSessions(data.sessions || []);
      }
    } catch (err) {
      logger.error('Error fetching sessions:', err);
    }
  }, []);

  useEffect(() => {
    fetchUserData();
    fetchActiveSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSetup2FA = async (method) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      if (method === 'totp') {
        const response = await fetch(`${BACKEND_URL}/api/auth/2fa/setup/totp`, { method: 'POST', headers: getHeaders() });
        if (response.ok) {
          const data = await response.json();
          setQrCodeUrl(data.qr_code);
          setTotpSecret(data.secret || '');
          setOtpauthUri(data.otpauth_uri || '');
          setShowQRCode(true);
        } else {
          setError((await response.json()).detail || 'Failed to setup 2FA');
        }
      } else if (method === 'email') {
        const response = await fetch(`${BACKEND_URL}/api/auth/2fa/setup/email`, { method: 'POST', headers: getHeaders() });
        if (response.ok) {
          setSuccess('Verification code sent to your email. Please check your inbox.');
          setShowQRCode(true);
        } else {
          setError((await response.json()).detail || 'Failed to setup Email 2FA');
        }
      } else if (method === 'sms') {
        setError('SMS 2FA requires Twilio configuration. Please contact administrator to enable this feature.');
      }
    } catch (err) {
      setError('Failed to setup 2FA. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setActionLoading(true);
    setError('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/2fa/verify/totp`, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ code: verificationCode }) });
      if (response.ok) {
        const data = await response.json();
        setSuccess('Two-factor authentication enabled successfully! Save your backup codes.');
        setShowQRCode(false);
        setVerificationCode('');
        fetchUserData();
        if (data.backup_codes) {
          alert(`IMPORTANT: Save these backup codes:\n\n${data.backup_codes.join('\n')}\n\nYou won't be able to see them again!`);
        }
      } else {
        setError((await response.json()).detail || 'Invalid code. Please try again.');
      }
    } catch (err) {
      setError('Failed to verify code. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable2FA = () => {
    setPasswordModalConfig({ title: 'Disable Two-Factor Authentication', description: 'This is a critical security action. Please enter your password and check your email to confirm.' });
    setPendingAction('disable_2fa');
    setShowPasswordModal(true);
  };

  const handlePasswordConfirm = async (password) => {
    setActionLoading(true);
    setError('');
    try {
      let endpoint = '';
      if (pendingAction === 'disable_2fa') endpoint = `${BACKEND_URL}/api/auth/2fa/disable/initiate`;
      else if (pendingAction === 'logout_all') endpoint = `${BACKEND_URL}/api/auth/sessions/logout-all/initiate`;
      else if (pendingAction === 'cancel_subscription') endpoint = `${BACKEND_URL}/api/users/subscription/cancel/initiate`;
      const response = await fetch(endpoint, { method: 'POST', headers: getHeaders(), body: JSON.stringify({ password }) });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message || 'Confirmation email sent. Please check your inbox.');
        setShowPasswordModal(false);
        setPendingAction(null);
      } else {
        setError(data.detail || 'Failed to complete action');
      }
    } catch (err) {
      setError('Failed to complete action. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStrictMode = async () => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(`${BACKEND_URL}/api/security/strict-mode`, {
        method: 'PUT', headers: getHeaders(),
        body: JSON.stringify({ strict_security_mode: !strictMode })
      });
      if (response.ok) {
        setStrictMode(!strictMode);
        const msg = `Strict Security Mode ${!strictMode ? 'enabled' : 'disabled'} successfully`;
        setSuccess(msg);
        toast.success(msg);
      } else {
        setError((await response.json()).detail || 'Failed to update security settings');
      }
    } catch (err) {
      logger.error('Error toggling strict mode:', err);
      setError('Failed to update security settings. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleLogoutAllDevices = () => {
    setPasswordModalConfig({ title: 'Logout All Devices', description: 'You will be logged out from all devices including this one. Please enter your password and check your email to confirm.' });
    setPendingAction('logout_all');
    setShowPasswordModal(true);
  };

  return {
    user, loading, activeSessions, showDeviceSessions, setShowDeviceSessions,
    twoFactorStatus, showQRCode, setShowQRCode, qrCodeUrl, totpSecret, otpauthUri,
    verificationCode, setVerificationCode,
    error, setError, success, setSuccess,
    actionLoading, strictMode, setStrictMode,
    showPasswordModal, setShowPasswordModal,
    pendingAction, setPendingAction, passwordModalConfig, setPasswordModalConfig,
    handleSetup2FA, handleVerify2FA, handleDisable2FA, fetchUserData,
    handlePasswordConfirm, handleToggleStrictMode, handleLogoutAllDevices,
  };
}
